import {
  type DashboardAttributes,
  type Pin,
  Realm,
} from "src/clients/analytics-dashboard";
import palette from "src/styles/tailwindColors.json";

export const PERFORMANCE_DASHBOARD_ID = "6b8947ed-aa52-4da8-9efa-d28ac19bdbf3";
export const PERFORMANCE_DASHBOARD_NAME = "Performance";
export const ANALYTICS_DASHBOARD_NAME = "Analytics";

// Distinguishes a fixed dashboard from an uncreated one (version 0),
// whose next write would be a create.
const FIXED_VERSION = -1;

const FLOW_ID_PLACEHOLDER = "{{FLOW_ID}}";

const KPI_LABEL_COLOR = palette.gray[500];
const KPI_VALUE_COLOR = palette.gray[800];
const KPI_LABEL_SIZE = 14;
const KPI_VALUE_SIZE = 28;
const KPI_VALUE_WEIGHT = 550;
// Fractions of the y scale, not pixels: SandboxedChart overrides `height`.
const KPI_LABEL_Y = 0.685;
const KPI_VALUE_Y = 0.365;

const RATE_LINE_COLOR = palette.red[500];

const PERCENTILE_DOMAIN = ["P50", "P90", "P95", "P99"] as const;
const PERCENTILE_RANGE = [
  palette.green[500],
  palette.yellow[500],
  palette.orange[500],
  palette.red[500],
];

const CLIENT_ERROR_CLASS = "Client error (4xx)";
const SERVER_ERROR_CLASS = "Server error (5xx)";
const ERROR_CLASS_DOMAIN = [CLIENT_ERROR_CLASS, SERVER_ERROR_CLASS] as const;
const ERROR_CLASS_RANGE = [palette.yellow[500], palette.orange[500]];

const dayGrid = (gate: string): string => `days AS (
  SELECT unnest(generate_series(
    date_trunc('day', CAST($time_range_start AS TIMESTAMP)),
    date_trunc('day', CAST($time_range_end AS TIMESTAMP) - INTERVAL 1 MICROSECOND),
    INTERVAL 1 DAY
  )) AS date
  WHERE EXISTS (SELECT 1 FROM ${gate})
)`;

const integerAxisLabelExpr = (format: string): string =>
  `datum.value % 1 ? '' : format(datum.value, '${format}')`;

const DAY_AXIS = {
  grid: false,
  labelAngle: 0,
  labelOverlap: "greedy",
  labelSeparation: 4,
  labelExpr: "timeFormat(datum.value, '%d %a')",
  title: null,
} as const;

const BOTTOM_LEGEND = { orient: "bottom", title: null } as const;

const PERFORMANCE_PIN_TEMPLATES: Pin[] = [
  {
    id: "40acf169-b491-5965-8c2a-40baef1f981e",
    title: "Metrics",
    description: null,
    layout: { column: 1, row: 1, width: 6, height: 2 },
    definition: {
      type: "chart",
      query: {
        query: `WITH stats AS (
  SELECT
    COUNT(*) AS total_decisions,
    COALESCE(SUM(CASE WHEN http_status_code >= 400 THEN 1 ELSE 0 END), 0) AS total_errors,
    approx_quantile(latency_ms, 0.90) AS p90_latency
  FROM flow_data
  WHERE flow_id = '${FLOW_ID_PLACEHOLDER}'
    AND environment IN $environment
    AND start_datetime >= $time_range_start
    AND start_datetime < $time_range_end
)
SELECT 1 AS position, 'Decision volume' AS label, format('{:,}', total_decisions) AS value FROM stats
UNION ALL
SELECT 2, 'Total errors', format('{:,}', total_errors) FROM stats
UNION ALL
SELECT 3, 'Error rate',
  CASE
    WHEN total_decisions = 0 THEN '—'
    ELSE format('{:.1f}%', 100.0 * total_errors / total_decisions)
  END
FROM stats
UNION ALL
SELECT 4, 'Response Time (P90)',
  CASE
    WHEN p90_latency IS NULL THEN '—'
    ELSE format('{:,.1f} ms', p90_latency)
  END
FROM stats
ORDER BY position
`,
        parameters: ["environment", "time_range_start", "time_range_end"],
      },
      chart_definition: {
        $schema: "https://vega.github.io/schema/vega-lite/v6.json",
        // No axes to anchor it, so Vega's default frame reads as a stray box.
        view: { stroke: null },
        encoding: {
          x: {
            field: "label",
            type: "nominal",
            sort: { field: "position" },
            axis: null,
          },
        },
        layer: [
          {
            mark: {
              type: "text",
              align: "center",
              baseline: "middle",
              color: KPI_LABEL_COLOR,
              fontSize: KPI_LABEL_SIZE,
            },
            encoding: {
              text: { field: "label" },
              y: {
                datum: KPI_LABEL_Y,
                type: "quantitative",
                scale: { domain: [0, 1] },
                axis: null,
              },
            },
          },
          {
            mark: {
              type: "text",
              align: "center",
              baseline: "middle",
              color: KPI_VALUE_COLOR,
              fontSize: KPI_VALUE_SIZE,
              fontWeight: KPI_VALUE_WEIGHT,
            },
            encoding: {
              text: { field: "value" },
              y: {
                datum: KPI_VALUE_Y,
                type: "quantitative",
                scale: { domain: [0, 1] },
                axis: null,
              },
            },
          },
        ],
      },
    },
  },
  {
    id: "153241d7-9f47-5130-92bf-062ee71aebb5",
    title: "Number of decisions over time",
    description: null,
    layout: { column: 1, row: 3, width: 6, height: 4 },
    definition: {
      type: "chart",
      query: {
        query: `WITH tally AS (
  SELECT
    date_trunc('day', start_datetime) AS date,
    flow_version_id AS version,
    COUNT(*) AS decisions
  FROM flow_data
  WHERE flow_id = '${FLOW_ID_PLACEHOLDER}'
    AND environment IN $environment
    AND start_datetime >= $time_range_start
    AND start_datetime < $time_range_end
  GROUP BY 1, 2
),
${dayGrid("tally")},
versions AS (SELECT DISTINCT version FROM tally)
SELECT
  days.date AS date,
  versions.version AS version,
  COALESCE(tally.decisions, 0) AS decisions
FROM days
CROSS JOIN versions
LEFT JOIN tally ON tally.date = days.date AND tally.version = versions.version
ORDER BY 1, 2
`,
        parameters: ["environment", "time_range_start", "time_range_end"],
      },
      chart_definition: {
        $schema: "https://vega.github.io/schema/vega-lite/v6.json",
        mark: { type: "bar", width: { band: 0.58 } },
        encoding: {
          // Ordinal band so the bars fill the bucket; a day formatted in SQL
          // would sort as a string and run 29, 30, 31, 01 across a month end.
          x: {
            field: "date",
            type: "ordinal",
            timeUnit: "yearmonthdate",
            axis: DAY_AXIS,
          },
          y: {
            field: "decisions",
            type: "quantitative",
            stack: "zero",
            axis: {
              tickCount: 5,
              tickMinStep: 1,
              labelExpr: integerAxisLabelExpr("~s"),
              title: null,
            },
          },
          color: {
            field: "version",
            type: "nominal",
            legend: BOTTOM_LEGEND,
          },
          order: { field: "version", type: "nominal" },
          tooltip: [
            {
              field: "date",
              type: "temporal",
              timeUnit: "yearmonthdate",
              title: "Date",
            },
            { field: "version", type: "nominal" },
            { field: "decisions", type: "quantitative", format: ",d" },
          ],
        },
        usermeta: {
          taktile: {
            specialDomains: [
              {
                channel: "color",
                field: "version",
                domain: "flow_version_id",
              },
            ],
          },
        },
      },
    },
  },
  {
    id: "2e0a60d9-0a34-5427-abbf-6f0f7435ffa1",
    title: "Duration over time",
    description: null,
    layout: { column: 1, row: 7, width: 6, height: 4 },
    definition: {
      type: "chart",
      query: {
        // Paired `unnest`s pivot the percentiles to rows off one grouped pass.
        // A day with no decisions joins to NULL, never 0: a zero-filled
        // percentile reads as a latency measurement that never happened.
        query: `WITH stats AS (
  SELECT
    date_trunc('day', start_datetime) AS date,
    unnest(['P50', 'P90', 'P95', 'P99']) AS percentile,
    unnest([
      approx_quantile(latency_ms, 0.50),
      approx_quantile(latency_ms, 0.90),
      approx_quantile(latency_ms, 0.95),
      approx_quantile(latency_ms, 0.99)
    ]) AS duration_ms
  FROM flow_data
  WHERE flow_id = '${FLOW_ID_PLACEHOLDER}'
    AND environment IN $environment
    AND start_datetime >= $time_range_start
    AND start_datetime < $time_range_end
    AND latency_ms IS NOT NULL
  GROUP BY 1
),
${dayGrid("stats")},
percentiles AS (SELECT DISTINCT percentile FROM stats)
SELECT
  days.date AS date,
  percentiles.percentile AS percentile,
  stats.duration_ms AS duration_ms
FROM days
CROSS JOIN percentiles
LEFT JOIN stats ON stats.date = days.date AND stats.percentile = percentiles.percentile
ORDER BY 1, 2
`,
        parameters: ["environment", "time_range_start", "time_range_end"],
      },
      chart_definition: {
        $schema: "https://vega.github.io/schema/vega-lite/v6.json",
        // Linear, not monotone: a spline through daily percentiles overshoots,
        // turning a one-day spike into a multi-day hump.
        mark: {
          type: "line",
          point: true,
          strokeWidth: 2,
          invalid: "break-paths-show-domains",
        },
        encoding: {
          x: {
            field: "date",
            type: "temporal",
            timeUnit: "yearmonthdate",
            axis: DAY_AXIS,
          },
          y: {
            field: "duration_ms",
            type: "quantitative",
            scale: { domainMin: 0, nice: true },
            axis: {
              tickCount: 5,
              labelExpr: "format(datum.value, '~s') + ' ms'",
              title: null,
            },
          },
          color: {
            field: "percentile",
            type: "nominal",
            scale: {
              domain: [...PERCENTILE_DOMAIN],
              range: [...PERCENTILE_RANGE],
            },
            legend: BOTTOM_LEGEND,
          },
          tooltip: [
            {
              field: "date",
              type: "temporal",
              timeUnit: "yearmonthdate",
              title: "Date",
            },
            { field: "percentile", type: "nominal" },
            {
              field: "duration_ms",
              type: "quantitative",
              format: ",.1f",
              title: "Duration (ms)",
            },
          ],
        },
      },
    },
  },
  {
    id: "db2799ab-95df-5937-8e29-db1a66bfab50",
    title: "Error rate over time",
    description: null,
    layout: { column: 1, row: 11, width: 6, height: 4 },
    definition: {
      type: "chart",
      query: {
        // A window with no errors still needs one row per day, or the rate
        // line has nothing to draw.
        query: `WITH tally AS (
  SELECT
    date_trunc('day', start_datetime) AS date,
    CASE WHEN http_status_code >= 400 THEN CAST(http_status_code AS VARCHAR) END AS error_code,
    COUNT(*) AS decisions
  FROM flow_data
  WHERE flow_id = '${FLOW_ID_PLACEHOLDER}'
    AND environment IN $environment
    AND start_datetime >= $time_range_start
    AND start_datetime < $time_range_end
  GROUP BY 1, 2
),
${dayGrid("tally")},
rates AS (
  SELECT
    date,
    100.0 * COALESCE(SUM(decisions) FILTER (WHERE error_code IS NOT NULL), 0) / SUM(decisions) AS error_rate
  FROM tally
  GROUP BY 1
),
codes AS (
  SELECT DISTINCT error_code FROM tally WHERE error_code IS NOT NULL
  UNION ALL
  SELECT 'No errors' FROM (SELECT 1) WHERE NOT EXISTS (
    SELECT 1 FROM tally WHERE error_code IS NOT NULL
  )
)
SELECT
  days.date AS date,
  codes.error_code AS error_code,
  CASE
    WHEN codes.error_code LIKE '4%' THEN '${CLIENT_ERROR_CLASS}'
    WHEN codes.error_code LIKE '5%' THEN '${SERVER_ERROR_CLASS}'
  END AS error_class,
  COALESCE(tally.decisions, 0) AS errors,
  rates.error_rate AS error_rate
FROM days
CROSS JOIN codes
LEFT JOIN rates ON rates.date = days.date
LEFT JOIN tally ON tally.date = days.date AND tally.error_code = codes.error_code
ORDER BY 1, 2
`,
        parameters: ["environment", "time_range_start", "time_range_end"],
      },
      chart_definition: {
        $schema: "https://vega.github.io/schema/vega-lite/v6.json",
        // Ordinal, so the line's points land on the bars' band centres.
        encoding: {
          x: {
            field: "date",
            type: "ordinal",
            timeUnit: "yearmonthdate",
            axis: DAY_AXIS,
          },
        },
        layer: [
          {
            mark: { type: "bar", width: { band: 0.58 } },
            encoding: {
              y: {
                field: "errors",
                type: "quantitative",
                stack: "zero",
                axis: {
                  tickCount: 5,
                  tickMinStep: 1,
                  labelExpr: integerAxisLabelExpr(",d"),
                  title: null,
                },
              },
              color: {
                field: "error_class",
                type: "nominal",
                scale: {
                  domain: [...ERROR_CLASS_DOMAIN],
                  range: [...ERROR_CLASS_RANGE],
                },
                legend: BOTTOM_LEGEND,
              },
              order: { field: "error_code", type: "nominal" },
              tooltip: [
                {
                  field: "date",
                  type: "temporal",
                  timeUnit: "yearmonthdate",
                  title: "Date",
                },
                { field: "error_code", type: "nominal", title: "Status" },
                { field: "errors", type: "quantitative", format: ",d" },
              ],
            },
          },
          {
            mark: {
              type: "line",
              // The point overlay is its own mark and does not inherit `color`.
              point: { color: RATE_LINE_COLOR },
              strokeWidth: 2,
              color: RATE_LINE_COLOR,
              invalid: "break-paths-show-domains",
            },
            encoding: {
              // One rate per day; the row set fans it out across the codes.
              y: {
                field: "error_rate",
                aggregate: "max",
                type: "quantitative",
                scale: { domainMin: 0 },
                axis: {
                  orient: "right",
                  grid: false,
                  tickCount: 5,
                  labelExpr: "format(datum.value, '.2~f') + '%'",
                  title: null,
                },
              },
              tooltip: [
                {
                  field: "date",
                  type: "temporal",
                  timeUnit: "yearmonthdate",
                  title: "Date",
                },
                {
                  field: "error_rate",
                  aggregate: "max",
                  type: "quantitative",
                  format: ".2~f",
                  title: "Error rate (%)",
                },
              ],
            },
          },
        ],
        resolve: { scale: { y: "independent" } },
      },
    },
  },
];

export const isPerformanceDashboard = (
  realm: Realm,
  dashboardId: string | undefined,
): boolean => realm === Realm.FLOWS && dashboardId === PERFORMANCE_DASHBOARD_ID;

export const performanceDashboard = (flowId: string): DashboardAttributes => ({
  version: FIXED_VERSION,
  members: [],
  name: PERFORMANCE_DASHBOARD_NAME,
  pins: Object.fromEntries(
    PERFORMANCE_PIN_TEMPLATES.map((pin) => [
      pin.id,
      {
        ...pin,
        definition: {
          ...pin.definition,
          query: {
            ...pin.definition.query,
            query: pin.definition.query.query.replaceAll(
              FLOW_ID_PLACEHOLDER,
              flowId,
            ),
          },
        },
      },
    ]),
  ),
});
